import { InputWithLabel, SelectWithLabel, IOSSwitch } from '@adotel/components'
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Hidden,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'

import TimePeriodForm from '../../../components/TimePeriodForm'
import { DateObject } from '../../../helpers/date'
import { getCurrenciesAvailable } from '../../../helpers/userData'
import { getDirection, getTranslate } from '../../../localization'
import { Restriction, Template, Weekday } from '../../../types/contractTemplate'
import { Currency } from '../../../types/currency'
import TemplateDatePicker from '../TemplateDatePicker'
import useStyles from './styles/index.styles'

type RestrictionType = {
  key: string
}

interface TemplateRestrictionsModalProps {
  onClose: () => void
  onConfirm: (t: Template) => void
}

export const TemplateRestrictionsModal: React.FC<
  TemplateRestrictionsModalProps
> = ({ onClose, onConfirm }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [dateObject, setdateObject] = useState<DateObject>({})
  const [indeterminate, setIndeterminate] = useState(false)
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [selectedRestrictions, setselectedRestrictions] = useState<
    RestrictionType[]
  >([])

  // it means confirm button has been clicked
  const [formHasBeenTouched, setformHasBeenTouched] = useState(false)
  const [template, settemplate] = useState<Template>({
    CurrencyDiscounts: [
      {
        Currency: 'IRR',
        DiscountPercent: 0
      }
    ],
    From: '',
    To: '',
    Restrictions: {
      CTA: undefined,
      CTD: undefined,
      MaxStay: undefined,
      MinStay: undefined,
      Status: undefined
    },
    Weekdays: {
      Saturday: true,
      Sunday: true,
      Monday: true,
      Tuesday: true,
      Wednesday: true,
      Thursday: true,
      Friday: true
    }
  })
  const booleanRestriction = (item: RestrictionType) => (
    <div>
      <Typography>{getTranslate(item.key)}</Typography>
      <IOSSwitch
        color="primary"
        checked={template.Restrictions[item.key as Restriction] === 1}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.checked ? 1 : 0
          settemplate({
            ...template,
            Restrictions: {
              ...template.Restrictions,
              [item.key as Restriction]: newValue
            }
          })
        }}
      />
    </div>
  )

  const inputRestriction = (item: RestrictionType) => (
    <div>
      <Typography className={classes.inputLabel}>
        {getTranslate(item.key)}
      </Typography>
      <InputWithLabel
        type="number"
        className={classes.inputNumber}
        placeholder={getTranslate(item.key) + ' ' + getTranslate('is required')}
        autoComplete="off"
        inputSize="small"
        value={
          template.Restrictions[item.key as Restriction] &&
          template.Restrictions[item.key as Restriction] !== 0
            ? template.Restrictions[item.key as Restriction]?.toString()
            : ''
        }
        onChange={(event) => {
          const newValue = parseInt(event.currentTarget.value, 10)
          if (newValue > 0 || (!newValue && newValue !== 0)) {
            settemplate({
              ...template,
              Restrictions: {
                ...template.Restrictions,
                [item.key as Restriction]: newValue
              }
            })
          }
        }}
        error={
          formHasBeenTouched && !template.Restrictions[item.key as Restriction]
        }
        helpertext={
          formHasBeenTouched && !template.Restrictions[item.key as Restriction]
            ? item.key + ' is required'
            : undefined
        }
      />
    </div>
  )

  const showRestrictions = (restriction: RestrictionType) => {
    switch (restriction.key) {
      case 'Status':
      case 'CTA':
      case 'CTD':
        return booleanRestriction(restriction)
      case 'MinStay':
      case 'MaxStay':
        return inputRestriction(restriction)
      default:
        return ''
    }
  }

  const handleConfirm = () => {
    setformHasBeenTouched(true)

    // check truthy
    if (
      // validation for at least one Weekday day selected
      Object.values(template.Weekdays).reduce(
        (a, item) => Number(a) + Number(item),
        0
      ) &&
      // validation for date selection
      template.From &&
      template.To &&
      // validation for MaxStay input
      (!selectedRestrictions.some(
        (selectedRestriction) => selectedRestriction.key === 'MaxStay'
      ) ||
        template.Restrictions.MaxStay ||
        template.Restrictions.MaxStay === 0) &&
      // validation for MinStay input
      (!selectedRestrictions.some(
        (selectedRestriction) => selectedRestriction.key === 'MinStay'
      ) ||
        template.Restrictions.MinStay ||
        template.Restrictions.MinStay === 0) &&
      // validation for Currency Discount array
      template.CurrencyDiscounts.length > 0 &&
      template.CurrencyDiscounts.every(
        (cd) => cd.Currency && cd.DiscountPercent
      )
    ) {
      onClose()
      onConfirm(template)
    }
  }
  useEffect(() => {
    if (dateObject) {
      settemplate({
        ...template,
        From: dateObject.gregorianStartDate || '',
        To: dateObject.gregorianEndDate || ''
      })
    }
  }, [dateObject])

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked
    const updatedWeekdays: any = {}

    Object.keys(template.Weekdays).forEach((day) => {
      updatedWeekdays[day] = checked
    })

    settemplate((prevTemplate) => ({
      ...prevTemplate,
      Weekdays: updatedWeekdays
    }))
  }

  const allDaysSelected = Object.keys(template.Weekdays).every(
    (day) => template.Weekdays[day as Weekday]
  )

  useEffect(() => {
    if (allDaysSelected) {
      setIndeterminate(false)
    } else {
      const anyDaySelected = Object.keys(template.Weekdays).some(
        (day) => template.Weekdays[day as Weekday]
      )
      setIndeterminate(anyDaySelected)
    }
  }, [template.Weekdays])

  return (
    <Modal open dir={getDirection()} onClose={onClose}>
      <Paper className={classes.modalPaper}>
        <div className={classes.formContainer}>
          <Hidden smDown>
            <div className={classes.datepickerContainer}>
              <TemplateDatePicker
                settemplate={settemplate}
                template={template}
              />
              <div className={classes.validationCommentary}>
                {formHasBeenTouched && (!template.From || !template.To) && (
                  <Typography variant="h5" color="error">
                    {getTranslate('Date is required')}
                  </Typography>
                )}
              </div>
            </div>
          </Hidden>
          <Hidden mdUp>
            <TimePeriodForm
              isOpen={timePickerSelected}
              setTimePickerSelected={setTimePickerSelected}
              dateObject={dateObject}
              onChange={(data: DateObject) => {
                setdateObject(data)
              }}
              onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
              setOnDoneClickForCloseDatePicker={
                setOnDoneClickForCloseDatePicker
              }
              enablePastDays
            />
          </Hidden>
          <div className={classes.weekDaysRoot}>
            <Typography className={classes.title} variant="h3">
              {getTranslate('Days')}
            </Typography>
            <div className={classes.weekDaysContainer}>
              <FormControlLabel
                control={
                  <Checkbox
                    color={indeterminate ? 'default' : 'primary'}
                    checked={allDaysSelected}
                    indeterminate={indeterminate}
                    onChange={handleSelectAllChange}
                  />
                }
                label={getTranslate('Select All')}
              />
              {Object.keys(template.Weekdays).map((weekDay) => (
                <FormControlLabel
                  key={weekDay}
                  control={
                    <Checkbox
                      color="primary"
                      checked={template.Weekdays[weekDay as Weekday]}
                      onChange={(event) => {
                        settemplate({
                          ...template,
                          Weekdays: {
                            ...template.Weekdays,
                            [weekDay as Weekday]: event.target.checked
                          }
                        })
                      }}
                    />
                  }
                  label={getTranslate(weekDay)}
                />
              ))}
              <div className={classes.validationCommentary}>
                {formHasBeenTouched &&
                  Object.values(template.Weekdays).reduce(
                    (a, item) => Number(a) + Number(item),
                    0
                  ) <= 0 && (
                    <Typography variant="h5" color="error">
                      {getTranslate('At least one weekday is required')}{' '}
                    </Typography>
                  )}
              </div>
            </div>
          </div>
          <div className={classes.changesBox}>
            <Typography
              variant="h3"
              className={[classes.title, classes.changesTitle].join(' ')}>
              {getTranslate('Changes to be made:')}
            </Typography>
            <div className={classes.changeSelector}>
              <div className={classes.discountContainer}>
                <Typography className={classes.inputLabel}>
                  {getTranslate('Discounts')}
                </Typography>
                {template.CurrencyDiscounts.map((item, index) => (
                  <div className={classes.discountRow} key={index}>
                    <InputWithLabel
                      className={classes.inputNumber}
                      type="number"
                      placeholder={getTranslate('example: ') + 20}
                      autoComplete="off"
                      inputSize="small"
                      endIcon={<div className={classes.percentSign}>%</div>}
                      value={item.DiscountPercent}
                      onChange={(event) => {
                        const newDiscountPercent = parseInt(
                          event.currentTarget.value,
                          10
                        )
                        if (
                          (newDiscountPercent >= 0 &&
                            newDiscountPercent <= 100) ||
                          !newDiscountPercent
                        ) {
                          settemplate({
                            ...template,
                            CurrencyDiscounts: [
                              ...template.CurrencyDiscounts.slice(0, index),
                              {
                                ...template.CurrencyDiscounts[index],
                                DiscountPercent: newDiscountPercent
                              },
                              ...template.CurrencyDiscounts.slice(index + 1)
                            ]
                          })
                        }
                      }}
                      error={
                        formHasBeenTouched &&
                        !item.DiscountPercent &&
                        item.DiscountPercent !== 0
                      }
                      helpertext={
                        formHasBeenTouched &&
                        !item.DiscountPercent &&
                        item.DiscountPercent !== 0
                          ? getTranslate('Discount Percent is required')
                          : undefined
                      }
                    />
                    <div className={classes.currencyContainer}>
                      <Typography>
                        {getTranslate(isSmallScreen ? 'Curr' : 'Currency')}:
                      </Typography>
                      <Select
                        value={item.Currency}
                        onChange={(event) => {
                          settemplate({
                            ...template,
                            CurrencyDiscounts: [
                              ...template.CurrencyDiscounts.slice(0, index),
                              {
                                ...template.CurrencyDiscounts[index],
                                Currency: event.target.value as Currency
                              },
                              ...template.CurrencyDiscounts.slice(index + 1)
                            ]
                          })
                        }}>
                        {getCurrenciesAvailable().map((currency) => (
                          <MenuItem
                            key={currency}
                            value={currency}
                            className={classes.menuItemRight}>
                            {currency}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div className={classes.Buttons}>
                      <IconButton
                        className={classes.Button}
                        onClick={() => {
                          settemplate({
                            ...template,
                            CurrencyDiscounts: [
                              ...template.CurrencyDiscounts,
                              {
                                Currency: 'IRR',
                                DiscountPercent: 0
                              }
                            ]
                          })
                        }}>
                        <AddCircleOutlineIcon />
                      </IconButton>
                      <IconButton
                        className={[
                          classes.Button,
                          index === 0 && classes.ButtonHide
                        ].join(' ')}
                        disabled={index === 0}
                        onClick={() => {
                          settemplate({
                            ...template,
                            CurrencyDiscounts: [
                              ...template.CurrencyDiscounts.slice(0, index),
                              ...template.CurrencyDiscounts.slice(index + 1)
                            ]
                          })
                        }}>
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
              <SelectWithLabel
                noHelperText
                fullWidth
                defaultValue={[]}
                SelectProps={{ multiple: true }}
                label={getTranslate(
                  'Select Restrictions that you want to change'
                )}
                onChange={(e) => {
                  const newSelectedItems = e.target.value as unknown as string[]
                  setselectedRestrictions(() =>
                    newSelectedItems.map((item) => ({
                      key: item
                    }))
                  )
                }}
                size="small">
                {Object.keys(template.Restrictions).map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <Divider className={classes.separator} />
              <div className={classes.restrictionTitleContainer}>
                <Typography variant="h5" className={classes.restrictionTitle}>
                  {getTranslate('Restrictions')}
                </Typography>
              </div>
              <div className={classes.restrictionsContainer}>
                {selectedRestrictions.map((item) => (
                  <div key={item.key} className={classes.restrictionItem}>
                    {showRestrictions(item)}
                  </div>
                ))}
              </div>
              {selectedRestrictions.length === 0 && (
                <Typography variant="h5">
                  {getTranslate('No restrictions has been selected yet')}
                </Typography>
              )}
            </div>
          </div>
          <Hidden mdUp>
            <div className={classes.buttonsContainer}>
              <Button variant="text" onClick={onClose}>
                {getTranslate('Cancel')}
              </Button>
              <Button
                variant="contained"
                className={classes.confirmButton}
                color="primary"
                onClick={handleConfirm}>
                {getTranslate('Confirm')}
              </Button>
            </div>
          </Hidden>
        </div>
        <Hidden smDown>
          <div className={classes.buttonsContainer}>
            <Button variant="text" onClick={onClose}>
              {getTranslate('Cancel')}
            </Button>
            <Button
              variant="contained"
              className={classes.confirmButton}
              color="primary"
              onClick={handleConfirm}>
              {getTranslate('Confirm')}
            </Button>
          </div>
        </Hidden>
      </Paper>
    </Modal>
  )
}
